import classNames from "classnames";
import React, { useState } from "react";
import { toast } from "react-toastify";
import CustomerDetailSubPage from "../../components/CustomerDetailSubPage";
import Layout from "../../components/Layout/Layout";
import Loading from "../../components/Loading";
import Table from "../../components/Table";
import {
  getCallingSelects,
  getCustomerGroupDateCustomers,
  getCustomerGroupsDateDetail,
  systemValues,
  updateCallingNote,
  updateCallingSelect,
  updateSystemValues,
} from "../../utils/api";
const CustomerDetailsGroup = () => {
  const [id, setId] = React.useState(null);
  const [totalCustomers, setTotalCustomers] = useState(null);
  const [customerData, setCustomerData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [date, setDate] = useState("");
  const [responseDates, setResponseDates] = useState({
    startDate: "",
    endDate: "",
  });

  // Calling selects state
  const [callingSelects, setCallingSelects] = useState([]);
  const [selectedCallingSelect, setSelectedCallingSelect] = useState(null);
  const [callingSelections, setCallingSelections] = useState({});
  const [callingNotes, setCallingNotes] = useState({});

  // Fetch calling selects
  const fetchCallingSelects = React.useCallback(async () => {
    const [success, data] = await getCallingSelects();
    if (success) {
      setCallingSelects(data);
    }
  }, []);

  // Initialize calling selections and notes from callingSelects
  React.useEffect(() => {
    const selections = {};
    const notes = {};
    callingSelects.forEach((item) => {
      // Set all items as selected by default if callingSelected is undefined
      selections[item.id] =
        item.callingSelected !== undefined ? item.callingSelected : true;
      notes[item.id] = item.callingNote || "";
    });
    setCallingSelections(selections);
    setCallingNotes(notes);
  }, [callingSelects]);

  const getCustomerDataById = async (id) => {
    setLoading(true);
    const [success, data] = await getCustomerGroupDateCustomers(id, date);
    if (success) {
      // Handle the new API response structure
      if (data.array) {
        setCustomerData(
          data.array.map((item) => ({ ...item, id: id, _id: item.id }))
        );
      } else {
        // Handle direct data response (sub API)
        setCustomerData(
          data.map((item) => ({ ...item, id: id, _id: item.id }))
        );
      }
    }
    setLoading(false);
  };

  const getCustomerGroupHandler = React.useCallback(async () => {
    setLoading(true);
    const [success, data] = await getCustomerGroupsDateDetail(date);
    if (success) {
      // Handle the new API response structure
      if (data.array) {
        setTotalCustomers(data.array);
        setResponseDates({
          startDate: data.startDate || "",
          endDate: data.endDate || "",
        });
      } else {
        setTotalCustomers(data);
      }
    }
    setLoading(false);
  }, [date]);

  React.useEffect(() => {
    getCustomerGroupHandler();
  }, [getCustomerGroupHandler]);

  React.useEffect(() => {
    const fetchSystemValues = async () => {
      const [success, res] = await systemValues();
      if (success) {
        setDate(res?.backDate || "");
      }
    };
    fetchSystemValues();
    fetchCallingSelects();
  }, [fetchCallingSelects]);

  const handleDateChange = async (newDate) => {
    setDate(newDate);
    // Update system values API
    const [success] = await updateSystemValues({ backDate: newDate });
    if (success) {
      // Optionally show success message
      console.log("System values updated successfully");
    }
  };

  // Handle calling select change
  const handleCallingSelectChange = async (customerId, isSelected) => {
    // Update local state immediately for better UX
    setCallingSelections((prev) => ({
      ...prev,
      [customerId]: isSelected,
    }));

    // Call API to update backend
    const [success] = await updateCallingSelect({
      id: customerId,
      callingSelected: isSelected,
    });

    if (!success) {
      // Revert on failure
      setCallingSelections((prev) => ({
        ...prev,
        [customerId]: !isSelected,
      }));
      toast.error("Failed to update selection");
    } else {
      // Refresh calling selects to get updated data
      await fetchCallingSelects();
    }
  };

  // Handle calling note change
  const handleCallingNoteChange = (customerId, note) => {
    // Update local state immediately
    setCallingNotes((prev) => ({
      ...prev,
      [customerId]: note,
    }));
  };

  // Handle calling note blur
  const handleCallingNoteBlur = async (customerId, note) => {
    // Call API to update backend when user finishes typing (on blur)
    const [success] = await updateCallingNote({
      id: customerId,
      callingNote: note,
    });

    if (!success) {
      toast.error("Failed to update calling note");
    } else {
      // Refresh calling selects to get updated data
      await fetchCallingSelects();
    }
  };

  return (
    <Layout>
      {loading && <Loading />}

      {/* Calling Selects Table */}
      {callingSelects.length > 0 && (
        <div className="px-2 mb-6">
          <h2 className="text-xl font-bold mb-3">Calling Selects</h2>
          <Table
            data={callingSelects}
            columns={[
              {
                title: "Select",
                dataIndex: "select",
                render: (item) => (
                  <input
                    type="checkbox"
                    checked={callingSelections[item.id] || false}
                    onChange={(e) => {
                      handleCallingSelectChange(item.id, e.target.checked);
                    }}
                    className="w-4 h-4"
                  />
                ),
              },
              {
                title: "Calling Note",
                dataIndex: "callingNote",
                render: (record) => (
                  <input
                    type="text"
                    value={callingNotes[record.id] || ""}
                    onChange={(e) =>
                      handleCallingNoteChange(record.id, e.target.value)
                    }
                    onBlur={(e) =>
                      handleCallingNoteBlur(record.id, e.target.value)
                    }
                    placeholder="Add note..."
                    className="border border-gray-300 px-2 py-1 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                  />
                ),
              },
              {
                title: "Name",
                dataIndex: "name",
                render: (record) => (
                  <button
                    onClick={() => setSelectedCallingSelect(record.id)}
                    className={`font-medium ${
                      selectedCallingSelect === record.id
                        ? "text-green-600"
                        : "text-blue-900"
                    }`}
                  >
                    {record.name}
                  </button>
                ),
              },
              {
                title: "Phone Number",
                dataIndex: "phoneNumber",
              },
              {
                title: "Balance",
                dataIndex: "balance",
                render: (record) => (
                  <span
                    className={
                      record.balance < 0 ? "text-red-600" : "text-green-600"
                    }
                  >
                    ₹{record.balance?.toFixed(2) || 0}
                  </span>
                ),
              },
              {
                title: "Order Tag Amount",
                dataIndex: "orderTagAmount",
                render: (record) => `${record.orderTagAmount || 0}`,
              },
            ]}
          />
        </div>
      )}

      <div className="px-2 mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2 ">
          Date Filter
        </label>
        <input
          type="number"
          value={date}
          onChange={(e) => handleDateChange(e.target.value)}
          className=" border-black px-3 py-2  border-2 ocus:outline-none focus:ring-2 focus:outline-none focus:border-transparent"
          placeholder="Enter date "
          min="1"
        />

        {(responseDates.startDate || responseDates.endDate) && (
          <div className="mt-2 text-sm text-gray-600">
            <p>
              Date Range: {responseDates.startDate} to {responseDates.endDate}
            </p>
          </div>
        )}
      </div>
      {totalCustomers && (
        <div className="flex flex-col md:flex-row gap-6   ">
          <Table
            className="w-auto"
            data={totalCustomers}
            columns={
              customerData.length > 0
                ? [
                    {
                      title: "Group Names",
                      dataIndex: "groupName",
                      render: (record) => (
                        <button
                          onClick={() => {
                            getCustomerDataById(record._id);
                            setId(record._id);
                          }}
                          className={` border-b  ${
                            customerData?.[0].id === record._id
                              ? "text-green-600 font-bold border-green-600"
                              : `text-blue-900 border-blue-900`
                          }`}
                        >
                          {record.groupName}
                        </button>
                      ),
                    },

                    {
                      title: "Products",
                      dataIndex: "products",
                      render: (record) =>
                        customerData?.[0].id === record._id && (
                          <div className="flex flex-col gap-2">
                            {record.products.map((item, index) => (
                              <p key={index}>
                                {item.productName}({item.quantity})
                              </p>
                            ))}
                            <p
                              className={classNames(
                                "font-bold ",
                                customerData?.reduce(
                                  (acc, item) => acc + item.balance,
                                  0
                                ) < 0
                                  ? "text-red-700"
                                  : "text-green-700"
                              )}
                            >
                              Total Balance :{" "}
                              {customerData
                                ?.reduce((acc, item) => acc + item.balance, 0)
                                ?.toFixed(2)}
                            </p>
                          </div>
                        ),
                    },
                  ]
                : [
                    {
                      title: "Group Name",
                      dataIndex: "groupName",
                      render: (record) => (
                        <button
                          onClick={() => getCustomerDataById(record._id)}
                          className="text-blue-900 border-b border-blue-900"
                        >
                          {record.groupName}
                        </button>
                      ),
                    },
                    {
                      title: "Total Customers",
                      dataIndex: "customers",
                    },
                  ]
            }
          />
          {customerData.length > 0 && (
            <CustomerDetailSubPage
              customerData={customerData}
              id={id}
              onSelectionChange={fetchCallingSelects}
            />
          )}
        </div>
      )}
    </Layout>
  );
};

export default CustomerDetailsGroup;
